import { useState, useEffect, useRef } from 'react';
import { STATUS_COLORS } from '../config/colors';

interface HorizontalBarChartData {
  label: string;
  values: { vehicle: string; value: number; color: string }[];
  count?: number;
  percentage?: number;
}

interface HorizontalBarChartProps {
  data: HorizontalBarChartData[];
  title?: string;
  description?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: string;
  showPercentage?: boolean;
  yAxisLabelX?: number; // Custom x position for y-axis label
  leftPadding?: number; // Custom left padding for y-axis labels and bars
}

export default function HorizontalBarChart({ 
  data, 
  title, 
  description,
  xAxisLabel = 'Value', 
  yAxisLabel = 'Category',
  height = 'h-96',
  yAxisLabelX = 25,
  leftPadding: customLeftPadding
}: HorizontalBarChartProps) {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const [, setContainerHeight] = useState<number>(300);
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Helper to parse Tailwind height classes (e.g., "h-72" = 288px)
  const parseHeightClass = (heightClass: string): number | null => {
    if (heightClass === 'h-auto') return null;
    const match = heightClass.match(/^h-(\d+)$/);
    if (match) {
      // Tailwind: h-{n} = n * 0.25rem = n * 4px
      return parseInt(match[1]) * 4;
    }
    return null;
  };

  // Calculate available height for SVG when fixed height is set
  const fixedHeightPx = parseHeightClass(height);
  const titleSectionHeight = title ? (description ? 80 : 50) : 0;
  const containerPadding = 32; // p-4 = 16px top + 16px bottom
  const availableHeightForSvg = fixedHeightPx 
    ? fixedHeightPx - titleSectionHeight - containerPadding - 4 // -4 for pb-1
    : null;

  // Calculate max value for scaling
  const maxValue = Math.max(...data.flatMap(d => d.values?.map(v => v.value) || []), 1);
  
  // Calculate nice round intervals for x-axis labels
  const calculateNiceTicks = (min: number, max: number, numTicks: number = 5): number[] => {
    if (max === min) {
      return [Math.max(0, Math.floor(min)), Math.ceil(max)];
    }
    
    const range = max - min;
    const rawStep = range / (numTicks - 1);
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep = rawStep / magnitude;
    
    let niceStep: number;
    if (normalizedStep <= 1) {
      niceStep = 1;
    } else if (normalizedStep <= 2) {
      niceStep = 2;
    } else if (normalizedStep <= 5) {
      niceStep = 5;
    } else {
      niceStep = 10;
    }
    
    niceStep = niceStep * magnitude;
    const niceMin = Math.floor(min / niceStep) * niceStep;
    const niceMax = Math.ceil(max / niceStep) * niceStep;
    
    const ticks: number[] = [];
    for (let tick = niceMin; tick <= niceMax; tick += niceStep) {
      ticks.push(tick);
    }
    
    return ticks;
  };

  const xAxisTicks = calculateNiceTicks(0, maxValue, 5);
  const displayMax = xAxisTicks[xAxisTicks.length - 1];
  const displayRange = displayMax || 1;

  const topPadding = 20;
  const bottomPadding = 70; // Increased to accommodate x-axis label spacing and tick values
  const leftPadding = customLeftPadding ?? 180; // Increased space for y-axis labels (customizable) - was 120
  const rightPadding = 100; // Space for value labels
  const barHeight = 22; // Compact bar height
  const barSpacing = 18; // Spacing between bar groups (increased for better readability)
  const totalBarHeight = barHeight + barSpacing;
  
  // Use parsed height if available (for fixed heights), otherwise calculate based on data
  const minChartHeight = 200;
  const dataBasedHeight = data.length * totalBarHeight + topPadding + bottomPadding;
  // For fixed heights, ensure we have enough space for all data items
  // If fixed height is too small, use data-based height instead
  // For h-auto, always use data-based height
  const calculatedHeight = (availableHeightForSvg !== null && height !== 'h-auto')
    ? Math.max(availableHeightForSvg, Math.max(minChartHeight, dataBasedHeight))
    : Math.max(minChartHeight, dataBasedHeight);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        // For fixed heights, use the parsed height; otherwise measure
        if (availableHeightForSvg !== null) {
          setContainerHeight(availableHeightForSvg);
        } else {
          // For auto height, measure or use default
          const rect = containerRef.current.getBoundingClientRect();
          setContainerHeight(Math.max(200, rect.height || 300));
        }
      }
    };
    // Small delay to ensure DOM is ready and styles are applied
    const timeoutId = setTimeout(updateDimensions, 10);
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDimensions);
      resizeObserver.disconnect();
    };
  }, [title, description, height, availableHeightForSvg]);

  // Guard against undefined/null data - AFTER all hooks are called
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className={`bg-card rounded-lg shadow-lg border border-border p-6 ${height} flex items-center justify-center`}>
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  const chartWidth = containerWidth - 40; // Account for container padding

  // Check if legend is needed (multiple values in any group)
  const needsLegend = data.length > 0 && data.some(group => (group.values || []).length > 1);
  const uniqueValues = new Map<string, { vehicle: string; color: string }>();
  if (needsLegend) {
    data.forEach(group => {
      (group.values || []).forEach(v => {
        if (!uniqueValues.has(v.vehicle)) {
          uniqueValues.set(v.vehicle, { vehicle: v.vehicle, color: v.color });
        }
      });
    });
  }

  return (
    <div ref={outerContainerRef} className={`bg-card rounded-lg shadow-lg border border-border p-4 ${height === 'h-auto' ? '' : height}`} style={height === 'h-auto' ? { minHeight: `${calculatedHeight + (title ? (description ? 80 : 50) : 0) + 32}px` } : {}}>
      {title && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {needsLegend && (
              <div className="flex items-center gap-3">
                {Array.from(uniqueValues.values()).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <div 
                      className="w-3 h-3 rounded" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-xs text-foreground font-medium">{item.vehicle}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {description && (
            <p className="text-muted-foreground text-xs">{description}</p>
          )}
        </div>
      )}
      <div 
        ref={containerRef} 
        className={`w-full overflow-x-auto pb-1 relative ${height === 'h-auto' ? 'overflow-y-hidden' : 'overflow-y-auto'}`}
        style={height === 'h-auto' ? { minHeight: `${calculatedHeight}px`, height: `${calculatedHeight}px` } : (availableHeightForSvg !== null ? { maxHeight: `${calculatedHeight}px`, height: `${calculatedHeight}px` } : { minHeight: `${calculatedHeight}px` })}
      >
        <svg
          ref={svgRef}
          width={chartWidth}
          height={calculatedHeight}
          style={{ display: 'block' }}
        >
          {/* Grid lines */}
          {xAxisTicks.map((tickValue) => {
            const x = leftPadding + (tickValue / displayRange) * (chartWidth - leftPadding - rightPadding);
            return (
              <g key={tickValue}>
                <line
                  x1={x}
                  y1={topPadding}
                  x2={x}
                  y2={calculatedHeight - bottomPadding}
                  stroke="rgb(var(--accent))"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                  opacity={0.4}
                  className="transition-opacity"
                />
                <text
                  x={x}
                  y={calculatedHeight - bottomPadding + 20}
                  textAnchor="middle"
                  fill="rgb(var(--foreground))"
                  fontSize="10"
                  fontWeight="500"
                  className="select-none"
                >
                  {tickValue.toLocaleString()}
                </text>
              </g>
            );
          })}

          {/* X-axis label */}
          <text
            x={chartWidth / 2}
            y={calculatedHeight - 10}
            textAnchor="middle"
            fill="rgb(var(--muted-foreground))"
            fontSize="11"
            fontWeight="500"
            className="select-none"
          >
            {xAxisLabel}
          </text>

          {/* Bars */}
          {data.map((group, index) => {
            const y = topPadding + index * totalBarHeight + barHeight / 2;
            const values = group.values || [];
            const maxValueInGroup = values.length > 0 ? Math.max(...values.map(v => v.value)) : 0;
            const isHovered = hoveredBar === index;

            // Helper to convert hex to rgba for glow effect
            const hexToRgba = (hex: string, alpha: number) => {
              const r = parseInt(hex.slice(1, 3), 16);
              const g = parseInt(hex.slice(3, 5), 16);
              const b = parseInt(hex.slice(5, 7), 16);
              return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };
            // Use green color for text glow (matching tab button hover color)
            const greenGlowColor = STATUS_COLORS.success; // success token
            const greenGlow = hexToRgba(greenGlowColor, 0.8);

            // If multiple values, render them stacked
            const hasMultipleValues = values.length > 1;
            const barSpacingBetween = hasMultipleValues ? 3 : 0; // Space between multiple bars
            const individualBarHeight = hasMultipleValues ? (barHeight - barSpacingBetween * (values.length - 1)) / values.length : barHeight;

            return (
              <g key={index}>
                {/* Bar background (subtle) */}
                <rect
                  x={leftPadding}
                  y={y - barHeight / 2}
                  width={chartWidth - leftPadding - rightPadding}
                  height={barHeight}
                  fill="rgb(var(--card))"
                  rx={6}
                  opacity={0.3}
                />
                
                {/* Render multiple bars if available */}
                {values.map((valueItem, valueIndex) => {
                  const barWidth = (valueItem.value / displayRange) * (chartWidth - leftPadding - rightPadding);
                  const barX = leftPadding;
                  const barY = hasMultipleValues 
                    ? y - barHeight / 2 + valueIndex * (individualBarHeight + barSpacingBetween)
                    : y - barHeight / 2;
                  const barGlowColor = hexToRgba(valueItem.color, 0.8);
                  
                  return (
                    <g key={valueIndex}>
                {/* Bar */}
                <rect
                  x={barX}
                        y={barY}
                  width={barWidth}
                        height={individualBarHeight}
                        fill={valueItem.color}
                  rx={6}
                  className="transition-all duration-200 cursor-pointer"
                  style={{
                    filter: isHovered ? `brightness(1.3) drop-shadow(0 0 8px ${barGlowColor})` : 'none',
                  }}
                  onMouseEnter={() => setHoveredBar(index)}
                  onMouseLeave={() => setHoveredBar(null)}
                />
                    </g>
                  );
                })}
                
                {/* Value labels - show after both blue and green bars */}
                {values.length > 0 && (() => {
                  const labels: JSX.Element[] = [];
                  
                  // Find blue bar (Before Optimisation)
                  const blueBarIndex = values.findIndex(v => v.color === STATUS_COLORS.info);
                  if (blueBarIndex >= 0) {
                    const blueBar = values[blueBarIndex];
                    const blueBarWidth = (blueBar.value / displayRange) * (chartWidth - leftPadding - rightPadding);
                    const blueFormattedValue = Number.isInteger(blueBar.value) 
                      ? blueBar.value.toLocaleString() 
                      : blueBar.value.toFixed(1);
                    
                    // Calculate the y position of the blue bar (if stacked, it's at a different y position)
                    const blueBarY = hasMultipleValues
                      ? y - barHeight / 2 + blueBarIndex * (individualBarHeight + barSpacingBetween) + individualBarHeight / 2
                      : y;
                    
                    // Position label after the blue bar's end, aligned with the blue bar's vertical center
                    // Blue bar label does NOT enlarge on hover
                    labels.push(
                      <text
                        key="blue"
                        x={leftPadding + blueBarWidth + 8}
                        y={blueBarY + 5}
                        textAnchor="start"
                        fill="rgb(var(--muted-foreground))"
                        fontSize="11"
                        fontWeight="500"
                        className="select-none pointer-events-none"
                      >
                        {blueFormattedValue}
                      </text>
                    );
                  }
                  
                  // Find green bar (After Optimisation)
                  const greenBarIndex = values.findIndex(v => v.color === STATUS_COLORS.success);
                  if (greenBarIndex >= 0) {
                    const greenBar = values[greenBarIndex];
                    const greenBarWidth = (greenBar.value / displayRange) * (chartWidth - leftPadding - rightPadding);
                    const greenFormattedValue = Number.isInteger(greenBar.value) 
                      ? greenBar.value.toLocaleString() 
                      : greenBar.value.toFixed(1);
                    
                    // Calculate the y position of the green bar (if stacked, it's at a different y position)
                    const greenBarY = hasMultipleValues
                      ? y - barHeight / 2 + greenBarIndex * (individualBarHeight + barSpacingBetween) + individualBarHeight / 2
                      : y;
                    
                    // Position label after the green bar's end, aligned with the green bar's vertical center
                    // Green bar label DOES enlarge on hover
                    labels.push(
                      <text
                        key="green"
                        x={leftPadding + greenBarWidth + 8}
                        y={greenBarY + 5}
                        textAnchor="start"
                        fill={isHovered ? greenGlowColor : 'rgb(var(--foreground))'}
                        fontSize={isHovered ? "14" : "12"}
                        fontWeight={isHovered ? "700" : "600"}
                        className="select-none pointer-events-none transition-all duration-200"
                        style={{
                          filter: isHovered ? `drop-shadow(0 0 6px ${greenGlow})` : 'none',
                        }}
                      >
                        {greenFormattedValue}
                      </text>
                    );
                  }
                  
                  // Fallback: if no blue or green found, show the last value
                  if (labels.length === 0 && values.length > 0) {
                    const lastBar = values[values.length - 1];
                    const lastBarWidth = (lastBar.value / displayRange) * (chartWidth - leftPadding - rightPadding);
                    const formattedValue = Number.isInteger(lastBar.value) 
                      ? lastBar.value.toLocaleString() 
                      : lastBar.value.toFixed(1);
                    
                    labels.push(
                      <text
                        key="fallback"
                        x={leftPadding + lastBarWidth + 8}
                        y={y + 5}
                        textAnchor="start"
                        fill={isHovered ? greenGlowColor : 'rgb(var(--foreground))'}
                        fontSize={isHovered ? "14" : "12"}
                        fontWeight={isHovered ? "700" : "600"}
                        className="select-none pointer-events-none transition-all duration-200"
                        style={{
                          filter: isHovered ? `drop-shadow(0 0 6px ${greenGlow})` : 'none',
                        }}
                      >
                        {formattedValue}
                      </text>
                    );
                  }
                  
                  return <>{labels}</>;
                })()}

                {/* Dashed white line to x-axis on hover */}
                {isHovered && maxValueInGroup > 0 && (
                  <line
                    x1={leftPadding + (maxValueInGroup / displayRange) * (chartWidth - leftPadding - rightPadding)}
                    y1={y}
                    x2={leftPadding + (maxValueInGroup / displayRange) * (chartWidth - leftPadding - rightPadding)}
                    y2={calculatedHeight - bottomPadding}
                    stroke="white"
                    strokeWidth={1.5}
                    strokeDasharray="4,4"
                    opacity={0.6}
                    className="pointer-events-none"
                  />
                )}

                {/* Y-axis label (category name) */}
                <text
                  x={leftPadding - 20}
                  y={y + 5}
                  textAnchor="end"
                  fill={isHovered ? greenGlowColor : 'rgb(var(--foreground))'}
                  fontSize={isHovered ? "14" : "12"}
                  fontWeight={isHovered ? "600" : "500"}
                  className="select-none pointer-events-none transition-all duration-200"
                  style={{
                    filter: isHovered ? `drop-shadow(0 0 6px ${greenGlow})` : 'none',
                  }}
                >
                  {group.label}
                </text>
              </g>
            );
          })}

          {/* Y-axis label */}
          <text
            x={yAxisLabelX}
            y={calculatedHeight / 2}
            textAnchor="middle"
            fill="rgb(var(--muted-foreground))"
            fontSize="11"
            fontWeight="500"
            className="select-none"
            transform={`rotate(-90, ${yAxisLabelX}, ${calculatedHeight / 2})`}
          >
            {yAxisLabel}
          </text>

        </svg>
      </div>
    </div>
  );
}


